import Box, { BoxProps } from '@liquidweb-dev/undercurrent/Box';
import CornerLeftUpIcon from '@liquidweb-dev/undercurrent/icons/lucide/CornerLeftUp';
import FolderIcon from '@liquidweb-dev/undercurrent/icons/lucide/Folder';
import GlobeIcon from '@liquidweb-dev/undercurrent/icons/lucide/Globe';
import Link from '@liquidweb-dev/undercurrent/Link';
import Typography, {
	TypographyProps,
} from '@liquidweb-dev/undercurrent/Typography';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';
import { LWApiPublicNotificationDetailsResultRawI } from '@liquidweb-dev/club-sauce/public/notifications/index.raw';
import ApplicationIcon from 'components/standard/ApplicationIcon';
import DisplayTypeIcon from 'components/standard/DisplayTypeIcon';
import ServerStatusBadgeFlat from 'components/standard/ServerStatusBadgeFlat';
import type { AssetDetails } from 'modules/queries/asset/useDetails';
import { Link as RouterLink } from 'react-router-dom';

const AttributeWrapper = ({ sx, ...rest }: BoxProps) => (
	<Box
		sx={combineSx({ display: 'flex', alignItems: 'center', gap: 50 }, sx)}
		{...rest}
	/>
);

const AttributeTypography = ({ sx, ...rest }: TypographyProps) => (
	<Typography
		variant="label-lg"
		sx={combineSx({ fontWeight: 500, whiteSpace: 'nowrap' }, sx)}
		{...rest}
	/>
);

export interface AttributesContentProps extends AssetDetails {
	notification?: LWApiPublicNotificationDetailsResultRawI;
}

const AttributesContent = ({
	displayType,
	domain,
	powerStatus,
	privateParent,
	project_name: projectName,
	status,
	templateApp,
	notification,
}: AttributesContentProps) => {
	const privateParentName = privateParent?.custom_name || privateParent?.domain;
	const privateParentPath = `/servers/${privateParent?.uniq_id}`;
	const { last_alert: lastAlert, description } = notification || {};

	return (
		<>
			<ServerStatusBadgeFlat
				status={status}
				powerStatus={powerStatus}
				notificationPrimary={description}
				notificationSecondary={lastAlert || ''}
			/>
			<AttributeWrapper>
				<GlobeIcon
					sx={({ vars, size }) => ({
						color: vars.palette.uc.icon.weak,
						height: size.xS,
						width: size.xS,
					})}
				/>
				<AttributeTypography>{domain}</AttributeTypography>
			</AttributeWrapper>
			{templateApp && (
				<AttributeWrapper>
					<ApplicationIcon
						application={templateApp}
						sx={({ vars, size }) => ({
							color: vars.palette.uc.icon.weak,
							height: size.xS,
							width: size.xS,
						})}
					/>
					<AttributeTypography>{templateApp}</AttributeTypography>
				</AttributeWrapper>
			)}
			{displayType && (
				<AttributeWrapper>
					<DisplayTypeIcon
						displayType={displayType}
						sx={({ vars, size }) => ({
							color: vars.palette.uc.icon.weak,
							height: size.xS,
							width: size.xS,
						})}
					/>
					<AttributeTypography>{displayType}</AttributeTypography>
				</AttributeWrapper>
			)}
			{projectName && (
				<AttributeWrapper>
					<FolderIcon
						sx={({ vars, size }) => ({
							color: vars.palette.uc.icon.weak,
							height: size.xS,
							width: size.xS,
						})}
					/>
					<AttributeTypography>{projectName}</AttributeTypography>
				</AttributeWrapper>
			)}

			{privateParentName && (
				<AttributeWrapper
					sx={{
						borderLeft: '1px solid',
						borderColor: ({ vars }) => vars.palette.uc.border.weak,
						alignSelf: 'stretch',
						paddingLeft: 175, // 14px - intentional to give appearance that border is centered,
					}}
				>
					<CornerLeftUpIcon
						sx={({ vars, size }) => ({
							color: vars.palette.uc.icon.weak,
							height: size.xS,
							width: size.xS,
						})}
					/>

					<Link
						component={RouterLink}
						to={privateParentPath}
						variant="label-lg"
						sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}
					>
						{privateParentName}
					</Link>
				</AttributeWrapper>
			)}
		</>
	);
};

export default AttributesContent;
